import { useState, useEffect, useRef } from 'react';
import BrandWatermark from '../../components/feature/BrandWatermark';
import { useSEO } from '../../utils/seo';

export default function BecomeADealerPage() {
  const [formData, setFormData] = useState({
    businessName: '',
    contactPerson: '',
    phone: '',
    email: '',
    city: '',
    state: '',
    businessType: '',
    monthlyVolume: '',
    productsInterested: [] as string[],
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://gi2merchants.com';

  useSEO({
    title: 'Become a GI2 Dealer - B2B Partnership Program',
    description: 'Join GI2 Merchants dealer network. Get bulk pricing, consistent stock, pan-India dispatch, and B2B support. Apply now to become a partner for printer consumables supply.',
    keywords: 'become dealer, GI2 dealer program, B2B partnership, authorized dealer, printer supplies dealer, wholesale partnership',
    canonical: `${siteUrl}/become-a-dealer`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Become a GI2 Dealer',
      description: 'Join a B2B supply network for printer consumables and lamination products across India',
      url: `${siteUrl}/become-a-dealer`,
      mainEntity: {
        '@type': 'Service',
        name: 'GI2 Dealer Partnership Program',
        provider: {
          '@type': 'Organization',
          name: 'GI2 Merchants LLP',
        },
        serviceType: 'B2B Partnership Program',
        areaServed: {
          '@type': 'Country',
          name: 'India',
        },
        offers: {
          '@type': 'Offer',
          description: 'Bulk pricing, consistent stock, pan-India dispatch, and dedicated B2B support',
        },
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleProductToggle = (product: string) => {
    setFormData(prev => ({
      ...prev,
      productsInterested: prev.productsInterested.includes(product)
        ? prev.productsInterested.filter(p => p !== product)
        : [...prev.productsInterested, product]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create WhatsApp message
    const whatsappMessage = `*🎯 GI2 Dealer Application*

*📋 Business Information*
Business Name: ${formData.businessName}
Business Type: ${formData.businessType || 'Not specified'}

*👤 Contact Information*
Contact Person: ${formData.contactPerson}
Phone: ${formData.phone}
Email: ${formData.email || 'Not provided'}
City: ${formData.city}
State: ${formData.state}

*📊 Business Profile*
Expected Monthly Volume: ${formData.monthlyVolume || 'Not specified'}

*🛒 Products Interested In:*
${formData.productsInterested.length > 0 ? formData.productsInterested.map(p => `• ${p}`).join('\n') : '• Not specified'}

*💬 Additional Information:*
${formData.message || 'None'}

---
_Submitted via GI2 Dealer Application Form_`;

    const encodedMessage = encodeURIComponent(whatsappMessage);
    const whatsappUrl = `https://wa.me/919711165605?text=${encodedMessage}`;

    // Small delay for better UX
    setTimeout(() => {
      setIsSubmitting(false);
      window.open(whatsappUrl, '_blank');
      
      // Reset form
      setFormData({
        businessName: '',
        contactPerson: '',
        phone: '',
        email: '',
        city: '',
        state: '',
        businessType: '',
        monthlyVolume: '',
        productsInterested: [],
        message: '',
      });
    }, 500);
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/919711165605?text=Hi, I want to become a GI2 dealer', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Reduced mobile padding */}
      <section className="relative bg-gradient-to-br from-slate-900 via-teal-900 to-slate-950 text-white py-12 md:py-20 overflow-hidden">
        <BrandWatermark variant="dark" position="top-left" size="large" />
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute top-0 right-0 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-teal-600 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-4 md:space-y-6">
            {/* Badge - Smaller on mobile */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full">
              <i className="ri-award-line text-teal-400 text-base md:text-lg"></i>
              <span className="text-xs md:text-sm font-semibold">B2B Partner Since 2011</span>
            </div>

            {/* Headline - Reduced size on mobile */}
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black leading-tight">
              Become a GI2 <span className="bg-gradient-to-r from-teal-400 to-orange-400 bg-clip-text text-transparent">Dealer</span>
            </h1>

            {/* Sub-headline - Smaller on mobile */}
            <p className="text-sm md:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Join our B2B supply network for printer consumables and lamination products across India
            </p>

            {/* Key Points - Compact grid on mobile */}
            <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-4 justify-center pt-2 md:pt-4 text-xs md:text-sm">
              {[
                { icon: 'ri-price-tag-3-line', text: 'Bulk Pricing' },
                { icon: 'ri-stock-line', text: 'Consistent Stock' },
                { icon: 'ri-truck-line', text: 'Pan-India Dispatch' },
                { icon: 'ri-customer-service-2-line', text: 'B2B Support' },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-1.5 md:gap-2 bg-white/5 backdrop-blur-sm px-2 md:px-3 py-1.5 md:py-2 rounded-lg border border-white/10">
                  <i className={`${item.icon} text-teal-400 text-sm md:text-base`}></i>
                  <span className="font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTAs - Stacked on mobile, smaller padding */}
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center pt-4 md:pt-6">
              <button
                onClick={scrollToForm}
                className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white text-sm md:text-base font-bold rounded-lg md:rounded-xl shadow-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line text-lg md:text-xl"></i>
                Apply to Become a Dealer
              </button>
              <button
                onClick={handleWhatsApp}
                className="px-6 md:px-8 py-3 md:py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white hover:text-teal-900 text-sm md:text-base font-bold rounded-lg md:rounded-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg md:text-xl"></i>
                Talk on WhatsApp
              </button>
            </div>

            {/* Trust Badges - Compact on mobile */}
            <div className="grid grid-cols-3 gap-3 md:gap-6 pt-6 md:pt-8 max-w-2xl mx-auto">
              {[
                { number: '2011', label: 'Established' },
                { number: '500+', label: 'Trade Partners' },
                { number: '1000+', label: 'SKUs' },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-4xl font-black text-teal-400">{stat.number}</div>
                  <div className="text-[10px] md:text-sm text-gray-400 font-medium mt-0.5 md:mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Partner Section - Reduced mobile padding */}
      <section className="py-10 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-6 md:mb-12">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 md:mb-4">
              Why Partner with GI2?
            </h2>
            <p className="text-sm md:text-lg text-gray-600">
              Built for serious B2B trade partnerships
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: 'ri-price-tag-3-line',
                title: 'Bulk Trade Pricing',
                description: 'Competitive wholesale rates',
                color: 'teal',
              },
              {
                icon: 'ri-stock-line',
                title: 'Assured Stock',
                description: 'Consistent availability',
                color: 'orange',
              },
              {
                icon: 'ri-truck-line',
                title: 'Fast Logistics',
                description: 'Pan-India dispatch',
                color: 'teal',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Account Manager',
                description: 'Dedicated B2B support',
                color: 'orange',
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Quality Products',
                description: 'Verified consumables',
                color: 'teal',
              },
              {
                icon: 'ri-hand-coin-line',
                title: 'Flexible Terms',
                description: 'B2B payment options',
                color: 'orange',
              },
            ].map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-lg md:rounded-xl p-4 md:p-6 border border-gray-200 hover:border-teal-600 hover:shadow-lg transition-all duration-300 group"
              >
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg md:rounded-xl bg-gradient-to-br ${
                  benefit.color === 'teal' ? 'from-teal-400 to-teal-600' : 'from-orange-400 to-orange-600'
                } flex items-center justify-center mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${benefit.icon} text-white text-lg md:text-2xl`}></i>
                </div>
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">{benefit.title}</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Can Become Section - Reduced mobile padding */}
      <section className="py-10 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-6 md:mb-12">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 md:mb-4">
              Who Can Become a Dealer?
            </h2>
            <p className="text-sm md:text-lg text-gray-600">
              We partner with established B2B businesses
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: 'ri-store-2-line',
                title: 'Retailers',
                description: 'Computer & office supply stores',
              },
              {
                icon: 'ri-building-line',
                title: 'Wholesalers',
                description: 'Bulk distributors',
              },
              {
                icon: 'ri-tools-line',
                title: 'Service Centers',
                description: 'Printer repair shops',
              },
              {
                icon: 'ri-briefcase-line',
                title: 'Corporate Vendors',
                description: 'Office supply vendors',
              },
            ].map((type, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-lg md:rounded-xl p-4 md:p-6 text-center hover:border-teal-600 hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-teal-600 transition-colors duration-300">
                  <i className={`${type.icon} text-teal-600 group-hover:text-white text-xl md:text-3xl transition-colors duration-300`}></i>
                </div>
                <h3 className="text-sm md:text-lg font-bold text-gray-900 mb-1 md:mb-2">{type.title}</h3>
                <p className="text-xs md:text-sm text-gray-600 leading-relaxed">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section - Reduced mobile padding */}
      <section className="py-10 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-6 md:mb-12">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 md:mb-4">
              How It Works
            </h2>
            <p className="text-sm md:text-lg text-gray-600">
              Simple 4-step dealer onboarding process
            </p>
          </div>

          {/* Vertical stepper on mobile, horizontal on desktop */}
          <div className="max-w-5xl mx-auto">
            {/* Mobile: Vertical */}
            <div className="md:hidden space-y-4">
              {[
                {
                  number: '1',
                  title: 'Submit Enquiry',
                  description: 'Fill the dealer application form',
                  icon: 'ri-file-text-line',
                },
                {
                  number: '2',
                  title: 'Verification',
                  description: 'Business verification & discussion',
                  icon: 'ri-shield-check-line',
                },
                {
                  number: '3',
                  title: 'Terms Shared',
                  description: 'Pricing & partnership terms',
                  icon: 'ri-file-list-3-line',
                },
                {
                  number: '4',
                  title: 'Start Ordering',
                  description: 'Begin your B2B partnership',
                  icon: 'ri-shopping-cart-line',
                },
              ].map((step, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {step.number}
                    </div>
                    {index < 3 && (
                      <div className="w-0.5 h-full bg-teal-200 mt-2"></div>
                    )}
                  </div>
                  <div className="pt-1">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center gap-2 mb-1">
                        <i className={`${step.icon} text-teal-600 text-lg`}></i>
                        <h3 className="text-sm font-bold text-gray-900">{step.title}</h3>
                      </div>
                      <p className="text-xs text-gray-600">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: Horizontal */}
            <div className="hidden md:grid md:grid-cols-4 gap-6 relative">
              {/* Connector Line Base */}
              <div className="absolute top-8 left-8 right-8 h-0.5 bg-gray-200 z-0"></div>
              
              {[
                {
                  number: '1',
                  title: 'Submit Enquiry',
                  description: 'Fill the dealer application form',
                  icon: 'ri-file-text-line',
                },
                {
                  number: '2',
                  title: 'Verification',
                  description: 'Business verification & discussion',
                  icon: 'ri-shield-check-line',
                },
                {
                  number: '3',
                  title: 'Terms Shared',
                  description: 'Pricing & partnership terms',
                  icon: 'ri-file-list-3-line',
                },
                {
                  number: '4',
                  title: 'Start Ordering',
                  description: 'Begin your B2B partnership',
                  icon: 'ri-shopping-cart-line',
                },
              ].map((step, index) => (
                <div key={index} className="relative z-10">
                  <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-600 hover:shadow-lg transition-all duration-300 text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mx-auto mb-4 text-white font-bold text-2xl shadow-lg">
                      {step.number}
                    </div>
                    <div className="mb-3">
                      <i className={`${step.icon} text-teal-600 text-3xl`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value Proof Section - Reduced mobile padding */}
      <section className="py-10 md:py-16 lg:py-20 bg-gradient-to-br from-teal-700 to-teal-900 text-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center mb-6 md:mb-10">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-2 md:mb-4">
              Dealer Benefits & Assurance
            </h2>
            <p className="text-sm md:text-lg text-teal-100">
              Clear, transparent, and reliable partnership terms
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
            {[
              { icon: 'ri-price-tag-3-line', text: 'Transparent Pricing' },
              { icon: 'ri-shield-check-line', text: 'No Hidden Charges' },
              { icon: 'ri-truck-line', text: 'Consistent Dispatch' },
              { icon: 'ri-customer-service-line', text: 'Support Escalation' },
            ].map((item, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg md:rounded-xl p-6 border border-white/20">
                <i className={`${item.icon} text-3xl md:text-4xl text-teal-300 mb-2 md:mb-3`}></i>
                <p className="text-xs md:text-sm font-semibold">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section - Reduced mobile padding */}
      <section ref={formRef} className="py-10 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-6 md:mb-10">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 md:mb-4">
                Dealer Application Form
              </h2>
              <p className="text-sm md:text-lg text-gray-600">
                Fill in your details and we'll connect with you on WhatsApp
              </p>
            </div>

            <form onSubmit={handleSubmit} className="relative bg-gray-50 rounded-xl md:rounded-2xl p-4 md:p-8 border border-gray-200 space-y-4 md:space-y-6">
              <BrandWatermark variant="subtle" position="top-right" size="medium" />
              {/* Business Information */}
              <div className="space-y-3 md:space-y-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-building-line text-teal-600"></i>
                  Business Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      required
                      value={formData.businessName}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="Your business name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                      Business Type *
                    </label>
                    <select
                      name="businessType"
                      required
                      value={formData.businessType}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                    >
                      <option value="">Select type</option>
                      <option value="Retailer">Retailer</option>
                      <option value="Wholesaler">Wholesaler</option>
                      <option value="Service Center">Service Center</option>
                      <option value="Corporate Vendor">Corporate Vendor</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-3 md:space-y-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-contacts-line text-teal-600"></i>
                  Contact Information
                </h3>
                
                <div className="grid md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                      Contact Person *
                    </label>
                    <input
                      type="text"
                      name="contactPerson"
                      required
                      value={formData.contactPerson}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="Full name"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                      Phone / WhatsApp *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="10-digit mobile"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="Your city"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      name="state"
                      required
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      placeholder="Your state"
                    />
                  </div>
                </div>
              </div>

              {/* Business Profile */}
              <div className="space-y-3 md:space-y-4">
                <h3 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                  <i className="ri-bar-chart-box-line text-teal-600"></i>
                  Business Profile
                </h3>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                    Expected Monthly Volume (Optional)
                  </label>
                  <select
                    name="monthlyVolume"
                    value={formData.monthlyVolume}
                    onChange={handleChange}
                    className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all cursor-pointer"
                  >
                    <option value="">Select range</option>
                    <option value="Below ₹50,000">Below ₹50,000</option>
                    <option value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</option>
                    <option value="₹1,00,000 - ₹3,00,000">₹1,00,000 - ₹3,00,000</option>
                    <option value="₹3,00,000 - ₹5,00,000">₹3,00,000 - ₹5,00,000</option>
                    <option value="Above ₹5,00,000">Above ₹5,00,000</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2 md:mb-3">
                    Products Interested In * <span className="text-teal-600 font-normal">(Select multiple)</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3">
                    {[
                      'Toner Cartridges',
                      'Generic Ink',
                      'OPC Drums',
                      'Toner Powder',
                      'Lamination Pouch',
                      'Lamination Machine',
                      'Photo Paper',
                      'Printer Parts',
                      'PVC Cards',
                    ].map((product) => (
                      <label
                        key={product}
                        onClick={() => handleProductToggle(product)}
                        className={`flex items-center gap-2 px-2 md:px-3 py-2 md:py-2.5 border-2 rounded-lg cursor-pointer transition-all text-xs md:text-sm ${
                          formData.productsInterested.includes(product)
                            ? 'border-teal-600 bg-teal-50 text-teal-900 font-semibold'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.productsInterested.includes(product)}
                          onChange={() => {}}
                          className="w-3.5 h-3.5 md:w-4 md:h-4 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
                        />
                        <span className="leading-tight">{product}</span>
                      </label>
                    ))}
                  </div>
                  {formData.productsInterested.length > 0 && (
                    <p className="text-xs text-teal-600 mt-2">
                      ✓ {formData.productsInterested.length} product{formData.productsInterested.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-1.5 md:mb-2">
                    Additional Information
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                    placeholder="Tell us more about your business requirements..."
                  ></textarea>
                  <div className="text-right text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2 md:pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || formData.productsInterested.length === 0}
                  className="w-full px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-500 hover:to-teal-600 text-white text-sm md:text-base font-bold rounded-lg md:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg md:text-xl"></i>
                      Redirecting to WhatsApp...
                    </>
                  ) : (
                    <>
                      <i className="ri-whatsapp-line text-lg md:text-xl"></i>
                      Submit via WhatsApp
                    </>
                  )}
                </button>
                {formData.productsInterested.length === 0 && (
                  <p className="text-xs text-red-600 text-center mt-2">
                    Please select at least one product to continue
                  </p>
                )}
              </div>

              {/* Reassurance */}
              <div className="text-center pt-2">
                <p className="text-xs md:text-sm text-gray-600 flex items-center justify-center gap-2">
                  <i className="ri-shield-check-line text-teal-600"></i>
                  Your information will be sent securely via WhatsApp
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Final CTA Section - Reduced mobile padding */}
      <section className="py-10 md:py-16 lg:py-20 bg-gradient-to-br from-slate-900 via-teal-900 to-slate-950 text-white relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-72 h-72 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob"></div>
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-4 md:space-y-6">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight">
              Ready to Partner with <span className="bg-gradient-to-r from-teal-400 to-orange-400 bg-clip-text text-transparent">GI2</span>?
            </h2>
            <p className="text-sm md:text-lg text-gray-300 max-w-2xl mx-auto">
              Join hundreds of successful B2B partners across India
            </p>

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center pt-2 md:pt-4">
              <button
                onClick={scrollToForm}
                className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 text-white text-sm md:text-base font-bold rounded-lg md:rounded-xl shadow-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-file-text-line text-lg md:text-xl"></i>
                Apply Now
              </button>
              <button
                onClick={handleWhatsApp}
                className="px-6 md:px-8 py-3 md:py-4 bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white hover:text-teal-900 text-sm md:text-base font-bold rounded-lg md:rounded-xl transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg md:text-xl"></i>
                WhatsApp Us
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
